/**
 * TherapistOnboarding - Zoom authentication and legal agreements orchestration
 *
 * Flow:
 * 1. Show Zoom sign-in button
 * 2. After auth, check if new signup (user.created_at within 10 seconds)
 * 3. If new signup + no agreements → show LegalAgreementsForm
 * 4. Otherwise → proceed to therapist panel
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useRole } from '../../context/RoleContext';
import { LegalAgreementsForm } from './LegalAgreementsForm';
import { supabase } from '../../lib/supabase';

export function TherapistOnboarding() {
  const { user, authState, signInWithZoom, signOut, isSigningIn } = useAuth();
  const { setRole, markAgreementsAsAccepted } = useRole();
  const [showAgreements, setShowAgreements] = useState(false);
  const [agreementsJustAccepted, setAgreementsJustAccepted] = useState(false);
  const [isCheckingAgreements, setIsCheckingAgreements] = useState(true);

  useEffect(() => {
    async function checkAgreementsInDB() {
      if (!user || authState !== 'signed-in') return;

      setIsCheckingAgreements(true);

      try {
        // Check database for existing consent records
        const { data, error } = await supabase
          .from('users')
          .select('professional_acknowledgment_accepted, terms_and_privacy_accepted, agreements_version')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[TherapistOnboarding] Error checking agreements:', error);
          // On error, show agreements to be safe
          setShowAgreements(true);
          setIsCheckingAgreements(false);
          return;
        }

        const hasAcceptedInDB = data?.professional_acknowledgment_accepted && data?.terms_and_privacy_accepted;

        console.log('[TherapistOnboarding] Agreement status from DB:', {
          userEmail: user.email,
          hasAcceptedInDB,
          version: data?.agreements_version,
        });

        if (hasAcceptedInDB) {
          // Sync localStorage with DB state
          console.log('[TherapistOnboarding] Agreements already accepted in DB, syncing localStorage');
          markAgreementsAsAccepted();
          setShowAgreements(false);
        } else {
          // Show agreements form
          console.log('[TherapistOnboarding] No agreements in DB, showing form');
          setShowAgreements(true);
        }
      } catch (err) {
        console.error('[TherapistOnboarding] Error:', err);
        setShowAgreements(true); // Show form on error to be safe
      } finally {
        setIsCheckingAgreements(false);
      }
    }

    checkAgreementsInDB();
  }, [user, authState, markAgreementsAsAccepted]);

  // If agreements need to be shown
  const handleBack = async () => {
    setShowAgreements(false);
    await signOut();
  };

  if (showAgreements) {
    return <LegalAgreementsForm onBack={handleBack} onComplete={() => {
      setShowAgreements(false);
      setAgreementsJustAccepted(true);
      // User metadata will be updated by useAgreements hook
      // App.tsx will redirect to TherapistPanel on next render
    }} />;
  }

  // Show loading while checking agreements in DB
  if (user && authState === 'signed-in' && isCheckingAgreements) {
    console.log('[TherapistOnboarding] Checking agreements in database...');
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-200 border-t-teal-600 mx-auto" style={{ margin: '0 auto 16px' }}></div>
        <p>Checking your account...</p>
      </div>
    </div>;
  }

  // If user is already signed in and agreements accepted (or just accepted), let App.tsx handle routing
  if (user && authState === 'signed-in' && (agreementsJustAccepted || !showAgreements)) {
    console.log('[TherapistOnboarding] Agreements accepted, App will redirect to panel');
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <p>Loading your control panel...</p>
    </div>;
  }

  // Show Zoom auth screen
  return (
    <div className="therapist-onboarding">
      <div className="therapist-onboarding-content">
        {/* Logo */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <img
            src="/assets/logos/transparent-eye-no-borders.png"
            alt="EMDR Sync"
            style={{ width: '120px', height: 'auto', margin: '0 auto' }}
          />
        </div>

        <h1 style={{ fontSize: '2rem', marginBottom: '16px', textAlign: 'center', fontWeight: 700, color: '#1F2937', letterSpacing: '-0.025em' }}>
          Therapist Login
        </h1>
        <p style={{ marginBottom: '32px', textAlign: 'center', color: '#6B7280', lineHeight: '1.6', fontSize: '1rem' }}>
          Welcome! Sign in with Zoom to begin delivering bilateral stimulation therapy to your clients
        </p>

        <button
          className="zoom-auth-button"
          onClick={signInWithZoom}
          disabled={isSigningIn}
        >
          {isSigningIn ? (
            <>
              <span className="loading-spinner"></span>
              Authenticating...
            </>
          ) : (
            'Sign in with Zoom'
          )}
        </button>

        <p className="auth-note">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '16px', height: '16px', display: 'inline', marginRight: '6px', verticalAlign: 'middle' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Control BLS parameters, manage client sessions, and access professional features
        </p>

        {/* Divider and Continue as Patient option */}
        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '12px', textAlign: 'center' }}>
            Not a therapist?
          </p>
          <button
            onClick={() => setRole('patient')}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: 'white',
              color: '#60A5FA',
              border: '2px solid #60A5FA',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#EFF6FF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            Continue as Patient
          </button>
        </div>
      </div>
    </div>
  );
}
